/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';

import { IPoNumberData } from './po-number.data';
import { IPoNumberProps } from './po-number.props.autogenerated';
import classnames from 'classnames';
import { AttributeTextValue, AttributeValueBase, Cart } from '@msdyn365-commerce/retail-proxy';
import { IModuleProps, INodeProps } from '@msdyn365-commerce-modules/utilities';
import { IModuleStateProps, withModuleState, statusType } from '@msdyn365-commerce-modules/checkout-utilities';
import { observer } from 'mobx-react';
import { action } from 'mobx';
import { getPOValidationAsync } from '../../actions/DataActionExtension.g';

export interface IPurchaseOrderViewState {
    isLoading: boolean;
    isError: boolean;
    isShowList: boolean;
    isShowSelected: boolean;
    isShowSaveButton: boolean;
    isShowEditButton: boolean;
    isShowCancelButton: boolean;
}

export interface IPoNumberViewProps extends IPoNumberProps<IPoNumberData> {
    poNumberWrapperProps: IModuleProps;
    poNumberInputProps: INodeProps;
    poNumberLabelProps: INodeProps;
    poNumberButtonProps: INodeProps;
    staticPoNumberDisplay: React.ReactNode;
    isSet: boolean;
    viewState: IPurchaseOrderViewState;
    error: React.ReactNode;
    poValidationMessage: React.ReactNode;
    isPoValidationMessage: boolean;
}

export interface IPoNumberModuleProps extends IPoNumberProps<IPoNumberData>, IModuleStateProps {}

export interface IPoNumberState {
    value: string;
    isSet: boolean;
    errorMessage: string | undefined;
    poValidationMessage: string | undefined;
    isPoValidationMessage: boolean;
}

/**
 *
 * PoNumber component
 * @extends {React.PureComponent<IPoNumberProps<IPoNumberData>>}
 */
// @ts-expect-error
@withModuleState
@observer
class PoNumber extends React.PureComponent<IPoNumberModuleProps, IPoNumberState> {
    private readonly poNumberInputId: string = 'po-number';
    // private readonly successPoValidationMessage: string = 'Validation succeeded';

    constructor(props: IPoNumberModuleProps) {
        super(props);
        this.state = { value: '', isSet: false, errorMessage: undefined, poValidationMessage: undefined, isPoValidationMessage: false };
    }

    private getPoNumberValue = (attributeValues: AttributeValueBase[] | undefined): string | null => {
        const attr = attributeValues?.find(attr => attr.Name === 'po-number') as AttributeTextValue;
        return attr?.TextValue || null;
    };

    public componentDidMount() {
        void this.init();
        const attributeValues = this.props.data.checkout.result?.checkoutCart.cart.AttributeValues;
        const poNumber = this.getPoNumberValue(attributeValues);
        if (poNumber) {
            this.setState({ value: poNumber, isSet: true });
        }
    }

    @action
    private readonly init = async (): Promise<void> => {
        const checkoutState = this.props.data.checkout.result;
        let status: statusType = 'pending';
        if (checkoutState) {
            if (checkoutState.checkoutCart.hasInvoiceLine) {
                status = 'disabled';
            } else {
                status = 'updating';
            }
        }
        this.props.moduleState.init({
            hasInitialized: true,
            onEdit: this.onEdit,
            onSubmit: this.onSubmit,
            isRequired: false,
            status
        });
    };

    private readonly onEdit = async (): Promise<void> => {
        this.onUpdating();
        if (this.props.data.checkout.result?.shouldEnableSinglePaymentAuthorizationCheckout) {
            await this.props.data.checkout.result.updateShouldCollapsePaymentSection({ newShouldCollapsePaymentSection: true });
        }
    };
    private readonly onUpdating = () => {
        this.props.moduleState.onUpdating();
    };
    private readonly onReady = () => {
        this.props.moduleState.onReady();
    };
    private readonly setError = (errorMessage: string | undefined, hasError: boolean): void => {
        this.props.moduleState.setHasError(hasError);
        this.setState({ errorMessage });
    };
    private readonly setPoValidationMessage = (poValidationMessage: string | undefined, isPoValidationMessage: boolean): void => {
        this.setState({ poValidationMessage, isPoValidationMessage });
    };

    private readonly onSubmit = async (): Promise<void> => {
        const {
            config: { requirePoNumber },
            resources: { poNumberErrorMessage }
        } = this.props;
        if (!requirePoNumber && !this.state.value) {
            this.onReady();
        } else if (!!this.state.value) {
            const resultStr = await getPOValidationAsync({ callerContext: this.props.context.actionContext }, this.state.value);
            if (resultStr === 'Validation succeeded') {
                this.savePoNumberToCart();
                this.setPoValidationMessage(undefined, false);
            } else {
                this.setPoValidationMessage(resultStr, true);
            }
        } else {
            this.setError(poNumberErrorMessage, true);
        }
    };

    private updatePoNumber = (e: React.ChangeEvent<HTMLInputElement>) => this.setState({ value: e.target.value });

    private handlePoNumberKeyup = (e: React.KeyboardEvent) => (e.keyCode === 13 ? this.addOrUpdatePoNumberToCart() : null);
    private savePoNumberToCart = () => {
        const cart = this.addOrUpdatePoNumberToCart();
        if (cart) {
            const checkoutState = this.props.data.checkout.result;
            if (!checkoutState) {
                return;
            }

            void checkoutState.checkoutCart.updateCart({ newCartObject: cart }).then(() => {
                this.setState({ isSet: !!this.state.value });
                this.onReady();
            });
        }
    };

    private renderStaticDisplay = (): React.ReactNode => (
        <p className={'eve-po-number__static'} key={'po-num-p'}>
            {this.props.resources.poNumberLabel}:{this.state.value}
        </p>
    );

    private addOrUpdatePoNumberToCart = (): Cart | undefined => {
        const attributeArr: AttributeValueBase[] = [];
        const poNumberIndex = this.props.data.checkout.result?.checkoutCart.cart.AttributeValues?.findIndex(
            attribute => attribute.Name === 'po-number'
        );
        const attributeObj = {
            '@odata.type': '#Microsoft.Dynamics.Commerce.Runtime.DataModel.AttributeTextValue',
            Name: 'po-number',
            ExtensionProperties: [],
            TextValue: this.state.value,
            TextValueTranslations: []
        };
        if (poNumberIndex === 0) {
            attributeArr.splice(poNumberIndex, 1, attributeObj);
        } else {
            attributeArr.push(attributeObj);
        }
        const cart: Cart = Object.assign({}, this.props.data.checkout.result?.checkoutCart.cart);
        if (cart) {
            cart.AttributeValues = attributeArr;
            return cart;
        }

        return undefined;
    };

    private renderErrorMessage = (): React.ReactNode => (
        <div className={'ms-checkout-po-number__error'}>
            <p className={'ms-checkout-po-number__error-message'}>{this.state.errorMessage}</p>
        </div>
    );

    private renderPoValidationMessage = (): React.ReactNode => (
        <div className={'ms-checkout-po-number__validation'}>
            <p className={'ms-checkout-po-number__validation-message'}>{this.state.poValidationMessage}</p>
        </div>
    );

    public render(): JSX.Element | null {
        const {
            moduleState: { isReady, isUpdating, hasError }
        } = this.props;
        const isLoading = this.props.data.checkout.result?.status !== 'READY' || false;

        const isError = !isLoading && hasError;
        const isShowList = !isLoading && isUpdating;
        const isShowSelected = !isLoading && !hasError && isReady;
        const viewProps: IPoNumberViewProps = {
            ...this.props,
            poNumberWrapperProps: {
                className: classnames(this.props.config.className, 'eve-po-number'),
                tag: 'div',
                moduleProps: { ...this.props }
            },
            poNumberInputProps: {
                className: classnames('eve-po-number__input'),
                value: this.state.value,
                onChange: this.updatePoNumber,
                onKeyUp: this.handlePoNumberKeyup,
                tag: 'input',
                id: this.poNumberInputId
            },
            poNumberLabelProps: {
                tag: 'label',
                htmlFor: this.poNumberInputId,
                className: 'ms-checkout-customer-account__label'
            },
            poNumberButtonProps: {
                tag: 'button',
                onClick: this.onSubmit,
                className: classnames('msc-btn', 'eve-po-number__button')
            },
            staticPoNumberDisplay: this.renderStaticDisplay(),
            isSet: this.state.isSet,
            viewState: {
                isLoading: isLoading,
                isError: isError,
                isShowList,
                isShowSelected,
                isShowSaveButton: isShowList,
                isShowEditButton: isShowSelected,
                isShowCancelButton: false
            },
            error: this.renderErrorMessage(),
            poValidationMessage: this.renderPoValidationMessage(),
            isPoValidationMessage: this.state.isPoValidationMessage
        };
        return this.props.renderView(viewProps);
    }
}

export default PoNumber;
